import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Spinner,
  Table
} from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { useBranchContext } from 'providers/BranchProvider';
import {
  TOutstandingReturnSupplier,
  TProcurementReturnFormItem
} from 'utils/types';
import { formatDate } from 'utils/formats';
import { toastConfig } from 'utils/toastConfig';
import BadgeStatus from 'utils/BadgeStatus';

type SupplierViewTabProps = {
  onFormCreated?: (formId: number) => void;
};

const SupplierViewTab = ({ onFormCreated }: SupplierViewTabProps) => {
  const { selectedBranch } = useBranchContext();
  const queryClient = useQueryClient();
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(
    null
  );
  const [selectedReturnIds, setSelectedReturnIds] = useState<number[]>([]);
  const [notes, setNotes] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const {
    data: suppliers = [],
    isLoading: suppliersLoading,
    refetch: refetchSuppliers
  } = useQuery({
    queryKey: [
      'outstandingReturnSuppliers',
      selectedBranch?.branchDomain
    ],
    enabled: !!selectedBranch?.branchDomain,
    queryFn: async () => {
      const { data } = await axiosInstance.get<TOutstandingReturnSupplier[]>(
        `/api/v1/${selectedBranch?.branchDomain}/procurementReturnForms/outstandingSuppliers`
      );
      return data;
    }
  });

  const {
    data: outstandingItems = [],
    isLoading: itemsLoading,
    refetch: refetchItems
  } = useQuery({
    queryKey: [
      'outstandingReturnsBySupplier',
      selectedBranch?.branchDomain,
      selectedSupplierId
    ],
    enabled: !!selectedBranch?.branchDomain && selectedSupplierId != null,
    queryFn: async () => {
      const { data } = await axiosInstance.get<TProcurementReturnFormItem[]>(
        `/api/v1/${selectedBranch?.branchDomain}/procurementReturnForms/outstanding`,
        { params: { supplierId: selectedSupplierId } }
      );
      return data;
    }
  });

  useEffect(() => {
    setSelectedReturnIds([]);
    setNotes('');
  }, [selectedSupplierId]);

  const selectedSupplier = useMemo(
    () => suppliers.find(s => s.supplierId === selectedSupplierId) || null,
    [suppliers, selectedSupplierId]
  );

  const selectedItems = useMemo(
    () =>
      outstandingItems.filter(item =>
        selectedReturnIds.includes(item.procurementReturnId)
      ),
    [outstandingItems, selectedReturnIds]
  );

  const selectedTotal = useMemo(
    () => selectedItems.reduce((sum, item) => sum + (item.amount || 0), 0),
    [selectedItems]
  );

  const allSelected =
    outstandingItems.length > 0 &&
    selectedReturnIds.length === outstandingItems.length;

  const toggleReturn = (procurementReturnId: number) => {
    setSelectedReturnIds(prev =>
      prev.includes(procurementReturnId)
        ? prev.filter(id => id !== procurementReturnId)
        : [...prev, procurementReturnId]
    );
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedReturnIds([]);
      return;
    }
    setSelectedReturnIds(outstandingItems.map(i => i.procurementReturnId));
  };

  const createReturnForm = async () => {
    if (!selectedSupplierId || selectedReturnIds.length === 0) {
      toast.error('Select at least one outstanding return.', toastConfig);
      return;
    }

    setIsCreating(true);
    try {
      const { data } = await axiosInstance.post(
        `/api/v1/${selectedBranch?.branchDomain}/procurementReturnForms`,
        {
          supplierId: selectedSupplierId,
          procurementReturnIds: selectedReturnIds,
          notes: notes || null
        }
      );
      toast.success('Return form created.', toastConfig);
      setSelectedReturnIds([]);
      setNotes('');
      await Promise.all([refetchSuppliers(), refetchItems()]);
      queryClient.invalidateQueries({ queryKey: ['procurementReturnForms'] });
      if (data?.procurementReturnFormId && onFormCreated) {
        onFormCreated(data.procurementReturnFormId);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to create return form.', toastConfig);
    } finally {
      setIsCreating(false);
    }
  };

  if (selectedSupplierId != null) {
    return (
      <div>
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <Button
              variant="link"
              className="p-0 mb-1"
              onClick={() => setSelectedSupplierId(null)}
            >
              ← Back to suppliers
            </Button>
            <h5 className="mb-0">
              Pending returns — {selectedSupplier?.supplierName}
            </h5>
          </div>
          <Button
            variant="primary"
            disabled={selectedReturnIds.length === 0 || isCreating}
            onClick={createReturnForm}
          >
            {isCreating ? 'Creating…' : 'Create Return Form'}
          </Button>
        </div>

        {itemsLoading ? (
          <div className="py-5 text-center">
            <Spinner animation="border" size="sm" />
          </div>
        ) : outstandingItems.length === 0 ? (
          <Alert variant="info" className="mb-0">
            No outstanding returns for this supplier.
          </Alert>
        ) : (
          <>
            <Form.Group className="mb-3" controlId="returnFormNotes">
              <Form.Label>Notes (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Notes for this return form"
              />
            </Form.Group>

            <div className="table-responsive">
              <Table hover className="fs-9 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <Form.Check
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        aria-label="Select all"
                      />
                    </th>
                    <th>Product</th>
                    <th>Invoice #</th>
                    <th>Delivery Date</th>
                    <th>Invoice Date</th>
                    <th>Status</th>
                    <th>Qty</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {outstandingItems.map(item => (
                    <tr key={item.procurementReturnId}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedReturnIds.includes(
                            item.procurementReturnId
                          )}
                          onChange={() =>
                            toggleReturn(item.procurementReturnId)
                          }
                          aria-label={`Select ${item.productName}`}
                        />
                      </td>
                      <td>
                        <Link
                          to={`/product/${encodeURIComponent(
                            selectedBranch?.branchDomain as string
                          )}/${item.productId}`}
                          className="text-decoration-none fw-bold"
                        >
                          {item.productName}
                        </Link>
                      </td>
                      <td>
                        {item.procurementInvoiceId ? (
                          <Link
                            to={`/procurement-invoices/${item.procurementInvoiceId}`}
                            className="text-decoration-none"
                          >
                            {item.procurementInvoiceNumber ||
                              item.procurementInvoiceId}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {item.deliveredOn
                          ? formatDate(item.deliveredOn)
                          : '—'}
                      </td>
                      <td>
                        {item.invoicedOn ? formatDate(item.invoicedOn) : '—'}
                      </td>
                      <td>
                        <BadgeStatus status={item.status} />
                      </td>
                      <td>{item.quantity}</td>
                      <td>{item.amount?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={6} className="text-end fw-bold">
                      Selected total ({selectedReturnIds.length} items)
                    </td>
                    <td className="fw-bold">
                      {selectedItems
                        .reduce((sum, i) => sum + (i.quantity || 0), 0)
                        .toLocaleString()}
                    </td>
                    <td className="fw-bold">
                      {selectedTotal.toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      {suppliersLoading ? (
        <div className="py-5 text-center">
          <Spinner animation="border" size="sm" />
        </div>
      ) : suppliers.length === 0 ? (
        <Alert variant="info" className="mb-0">
          No suppliers with outstanding returns.
        </Alert>
      ) : (
        <div className="table-responsive">
          <Table hover className="fs-9 align-middle">
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Outstanding Returns</th>
                <th>Quantity</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((supplier: TOutstandingReturnSupplier) => (
                <tr key={supplier.supplierId}>
                  <td>
                    <Button
                      variant="link"
                      className="p-0 text-decoration-none fw-bold"
                      onClick={() => setSelectedSupplierId(supplier.supplierId)}
                    >
                      {supplier.supplierName}
                    </Button>
                  </td>
                  <td>{supplier.outstandingCount}</td>
                  <td>{supplier.outstandingQuantity}</td>
                  <td>{supplier.outstandingAmount?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default SupplierViewTab;
